import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const photo = {
  title: "Photography",
  chapter: "01",
  tagline: "Frames engineered like film stills",
  rows: [
    ["First Hour", "$325 + GST"],
    ["Additional Hourly", "$130 / hr + GST"],
    ["Half-Day (6h)", "$650 + GST"],
    ["Full-Day (12h)", "$1,040 + GST"],
  ],
};
const video = {
  title: "Videography",
  chapter: "02",
  tagline: "Cinematic execution, no shortcuts",
  rows: [
    ["First Hour", "$325 + GST"],
    ["Additional Hourly", "$130 / hr + GST"],
    ["Half-Day (6h)", "$650 + GST"],
    ["Full-Day (12h)", "$1,040 + GST"],
  ],
};
const weddings = {
  title: "Weddings",
  chapter: "03",
  tagline: "Tiered base packages, tailored per commission",
  rows: [
    ["Micro (2 hours)", "By quote"],
    ["Half-Day (6 hours)", "By quote"],
    ["Full-Day (12 hours)", "By quote"],
    ["Multi-Day (3–5 days)", "By quote"],
  ],
};
const post = {
  title: "Post-Production",
  chapter: "04",
  tagline: "Colour, sound, retouch. Or receive RAW.",
  rows: [
    ["Editing Rate", "50% of shoot-day hourly"],
    ["RAW Delivery", "Available at additional cost"],
    ["Colour Grading", "DaVinci Resolve"],
    ["Retouch", "AI-assisted precision"],
  ],
};
const aerial = {
  title: "Aerials / Drone",
  chapter: "05",
  tagline: "Add-on to any commission",
  rows: [
    ["Availability", "Add-on to any commission"],
    ["Pricing Basis", "Location, flight time, insurance, event"],
    ["Regulation / Spotter", "As required by site"],
    ["Insurance", "Held on retainer"],
  ],
};

function Tier({ tier, i, aside }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 0.9, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
      className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 border-t border-white/5 pt-10 md:pt-14"
      data-testid={`pricing-${tier.title.toLowerCase().replace(/\W+/g, '-')}`}
    >
      <div className="md:col-span-3">
        <div className="numeral text-[7rem] md:text-[10rem] leading-none">{tier.chapter}</div>
      </div>
      <div className="md:col-span-4">
        <div className="h-eyebrow mb-3">Discipline</div>
        <h3 className="h-display text-3xl md:text-5xl leading-[1.05]">{tier.title}</h3>
        <p className="font-serif-editorial italic text-lg md:text-xl mt-4 text-[var(--gold)]">{tier.tagline}</p>
        {aside && <p className="font-serif-editorial text-base md:text-lg text-[var(--ink)]/70 leading-relaxed mt-6">{aside}</p>}
      </div>
      <div className="md:col-span-5">
        <ul className="divide-y divide-white/5">
          {tier.rows.map(([k, v]) => (
            <li key={k} className="flex items-baseline justify-between py-4">
              <span className="font-serif-editorial text-lg text-[var(--ink)]/80">{k}</span>
              <span className="font-sans-display uppercase tracking-[0.18em] text-sm text-[var(--gold)]">{v}</span>
            </li>
          ))}
        </ul>
      </div>
    </motion.article>
  );
}

export default function Services() {
  return (
    <main className="pt-24 md:pt-28" data-testid="page-services">
      <section className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="h-eyebrow mb-6">§ Services & Investment</div>
        <h1 className="h-display text-5xl md:text-7xl leading-[0.95] max-w-4xl">
          Transparent, modular,<br/>
          <span className="italic font-serif-editorial text-[var(--gold)] font-light">unapologetically studio-grade.</span>
        </h1>
        <p className="font-serif-editorial text-lg md:text-xl leading-relaxed text-[var(--ink)]/80 mt-10 max-w-2xl">
          Rates are the starting point of the conversation, not the entire conversation. Every commission is scoped privately — the numbers below are the vocabulary we use to build it.
        </p>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-12 mt-20 md:mt-28 space-y-16 md:space-y-20">
        <Tier tier={photo} i={0} />
        <Tier tier={video} i={1} />
        <Tier tier={weddings} i={2} aside="Ceremonies, elopements and multi-day celebrations are quoted individually. Micro packages honour intimacy; multi-day packages honour narrative." />
        <Tier tier={post} i={3} aside="Post-production is optional. Clients may opt to receive RAW files and edit externally &mdash; RAW file delivery is available at an additional cost." />
        <Tier tier={aerial} i={4} aside="Aerial capture can be added to any commission. Pricing varies with location, flight time, insurance and the nature of the event. Every flight is regulation-compliant, insured and spotter-supported." />
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 md:py-28 mt-20 md:mt-24 border-t border-white/5 text-center">
        <div className="h-eyebrow mb-6">§ Next</div>
        <h2 className="h-display text-4xl md:text-6xl leading-[0.95]">
          Let&rsquo;s price the <span className="italic font-serif-editorial text-[var(--gold)] font-light">actual project.</span>
        </h2>
        <Link
          to="/contact"
          data-testid="services-cta-consultation"
          className="inline-flex items-center gap-3 mt-10 bg-[var(--royal)] hover:bg-[var(--royal-soft)] text-white px-10 py-5 font-sans-display uppercase tracking-[0.3em] text-[11px] transition-colors"
        >
          Request a Private Consultation →
        </Link>
      </section>
    </main>
  );
}
